import React, { useState } from 'react';
import {
    Box, Typography, Paper, Button, Container,
    TextField, IconButton, InputAdornment,
    Alert, Avatar
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getBaseDomain, isCentralDomain, getSubdomain } from '../api';


const LoginPage = () => {
    const navigate = useNavigate();
    const { saveLoginResponse } = useAuth();

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Use centralized helper functions
    const detectedSubdomain = getSubdomain();
    const isCentral = isCentralDomain();
    const baseDomain = getBaseDomain();


    const [searchSubdomain, setSearchSubdomain] = useState('');
    const storeName = detectedSubdomain ? detectedSubdomain.toUpperCase() : 'Centering Supply';

    const handleGoToStore = (e) => {
        e.preventDefault();
        if (searchSubdomain) {
            const protocol = window.location.protocol;
            const port = window.location.port ? `:${window.location.port}` : '';
            window.location.href = `${protocol}//${searchSubdomain}.${baseDomain}${port}/login`;
        }

    };


    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let protocol = process.env.REACT_APP_API_PROTOCOL || 'http';
            let port = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';
            
            // Dynamic local fallback if accessing via localhost
            if (window.location.hostname.includes('localhost')) {
                protocol = 'http';
                port = ':8000';
            }
            
            const apiUrl = `${protocol}://${window.location.hostname}${port}/login`;


            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    ...formData,
                    subdomain: detectedSubdomain
                })
            });

            const data = await response.json();

            if (response.ok) {
                if (data.role === 'super_admin') {
                    // Super Admin — simple redirect, no branch context needed
                    localStorage.setItem('csm_user', JSON.stringify(data.user));
                    localStorage.setItem('csm_role', data.role);
                    localStorage.setItem('csm_token', 'mock_token_' + Date.now());
                    window.location.href = data.redirect_url;
                    return;
                }

                // Tenant login — save full session (user, tenant, branches)
                const session = saveLoginResponse(data);

                // Check subdomain: are we already on the tenant's subdomain?
                const currentHost = window.location.hostname;
                const targetUrl = new URL(data.redirect_url);
                const isSameHost = targetUrl.hostname === currentHost;

                if (!isSameHost) {
                    // Cross-subdomain transfer: attach full session as URL param
                    const sessionStr = btoa(JSON.stringify({
                        session,
                        role: data.role,
                        token: 'mock_token_' + Date.now(),
                    }));
                    targetUrl.searchParams.set('session_transfer', sessionStr);

                    // For Company Admin on multi-branch: land on /select-branch
                    if (session.isCompanyAdmin && session.isMultiBranch) {
                        targetUrl.pathname = '/select-branch';
                    }
                    window.location.href = targetUrl.toString();
                    return;
                }

                // Same subdomain — decide where to go
                if (session.isCompanyAdmin && session.isMultiBranch) {
                    navigate('/select-branch');
                } else {
                    navigate('/dashboard');
                }
            } else {
                setError(data.message || 'लॉगिन अयशस्वी.');
            }
        } catch (error) {
            setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #1B5E20 0%, #0FA958 100%)',
            py: 4
        }}>
            <Container maxWidth="xs">
                <Paper elevation={12} sx={{
                    p: 4,
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)'
                }}>
                    <Avatar sx={{ m: 1, bgcolor: '#1B5E20', width: 56, height: 56 }}>
                        <LockOutlinedIcon fontSize="large" />
                    </Avatar>

                    <Typography component="h2" variant="h4" sx={{ mt: 2, fontWeight: 800, color: '#102A43', textAlign: 'center', wordBreak: 'break-all' }}>
                        {detectedSubdomain ? storeName : 'Find Your Store'}
                    </Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 4, textAlign: 'center' }}>
                        {detectedSubdomain ? 'तुमच्या स्टोअरमध्ये लॉगिन करा' : 'तुमच्या स्टोअरचे नाव (Subdomain) टाका'}
                    </Typography>

                    {error && <Alert severity="error" sx={{ width: '100%', mb: 3 }}>{error}</Alert>}

                    {/* If on Central Domain: Show Store Search only (Super Admin uses /csmadmin) */}
                    {!detectedSubdomain ? (
                        <Box component="form" onSubmit={handleGoToStore} sx={{ mt: 1, width: '100%' }}>
                            <TextField
                                margin="normal" required fullWidth
                                label="स्टोअरचे नाव (उदा. amolshop)"
                                variant="filled"
                                value={searchSubdomain}
                                onChange={(e) => setSearchSubdomain(e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">.{baseDomain}</InputAdornment>,
                                }}

                                sx={{ backgroundColor: '#F0F4F8' }}
                            />
                            <Button
                                type="submit" fullWidth variant="contained"
                                sx={{
                                    mt: 3, py: 1.5, fontWeight: 'bold', backgroundColor: '#1B5E20',
                                    '&:hover': { backgroundColor: '#0C3D12' }
                                }}
                            >
                                स्टोअरकडे जा
                            </Button>
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                                <Typography variant="body2">
                                    खाते नाही? <a href="/" style={{ color: '#1B5E20', fontWeight: 'bold', textDecoration: 'none' }}>नवे स्टोअर उघडा</a>
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        /* If on Subdomain: Show Actual Login Form */
                        <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="ईमेल पत्ता"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                variant="filled"
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                sx={{ backgroundColor: '#F0F4F8' }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="पासवर्ड"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                variant="filled"
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                sx={{ backgroundColor: '#F0F4F8' }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    mt: 4,
                                    mb: 2,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    backgroundColor: '#1B5E20',
                                    '&:hover': { backgroundColor: '#0C3D12' },
                                    borderRadius: 2
                                }}
                                startIcon={<LoginIcon />}
                            >
                                {loading ? 'लॉगिन होत आहे...' : 'लॉगिन करा'}
                            </Button>

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography variant="body2" sx={{ color: '#627D98' }}>
                                    चुकीचे स्टोअर? <a href="/login" style={{ color: '#1B5E20', textDecoration: 'none' }}>बदला</a>
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>

                <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 5 }}>
                    {'© '} Rental Book {new Date().getFullYear()} | Powered by e.Sense InfoSolutions
                </Typography>
            </Container>
        </Box>
    );
};

export default LoginPage;
